// DropPin Outbox — v0.17.3
// ---------------------------------------------------------------------
// Tiny IndexedDB-backed queue for API mutations that failed because
// the device was offline. Once the device comes back online, the
// outbox drains in FIFO order and posts each queued request.
//
// Use case: a subby on a dodgy-reception site marks a defect
// rectified, photo + comment attached. Their POST fails because
// reception just dropped. v0.17.2 just lost that work; v0.17.3
// queues it and replays when reception returns.
//
// Schema:
//   record = {
//     id           autoincrement primary key
//     method       'POST' | 'PATCH' | 'DELETE'
//     path         '/api/defects' etc
//     body         the JSON payload (object, not stringified)
//     queuedAt     Date.now() ms
//     identityKey  who queued it (v0.118.0) — see outbox-identity.js.
//                  Absent on rows queued before v0.118.0; those are
//                  never replayed automatically.
//     bytesKey     a photo's copies in 'droppin-outbox-bytes' (v0.255.0)
//     blobMeta     { copy: { type, size } } for those copies   (v0.255.0)
//     blobs        { copy: { bytes, type } } inline — rows queued before
//                  v0.255.0 only, until moved (v0.174.0)
//   }
//
// API:
//   addToOutbox(method, path, body)  → Promise<localId>
//   listOutbox()                      → Promise<record[]>  (light: no bytes)
//   getRowBlobs(id, keys?)            → Promise<{copy}|null> one row's bytes (v0.255.0)
//   removeFromOutbox(id)              → Promise<boolean> (false: not removed, v0.256.0)
//   countOutbox()                     → Promise<number>
//   subscribeOutbox(fn)               → unsubscribe()   — fires on every change
//   drainOutbox(fetcher?, opts?)      → Promise<DrainResult>
//   getSyncResume() / clearSyncResume() → a sync to carry on with  (v0.256.0)
//   inspectOutbox()                   → Promise<{ pending, blocked }>  (v0.118.0)
//   discardOutboxItems(ids)           → Promise<void>                  (v0.118.0)
//   adoptUnstampedItems()             → Promise<number>                (v0.118.0)
//   getSyncLog() / clearSyncLog()     → last-sync history               (v0.118.0)
//
// Errors: every helper swallows IDB errors and resolves to a sensible
// default (empty list / 0). Better to lose offline-sync than to
// crash the app because IDB is throwing.
//
// ---------------------------------------------------------------------
// v0.256.0 — A SYNC CARRIES ON AFTER THE iPAD SLEEPS.
//
// 2026-10-21. Brett: "sync on droppin stopped again. i think it was when the
// ipad went to sleep. We cant have that." A sleeping iPad freezes the page: the
// upload in flight fails or hangs, and on waking the Wi-Fi takes seconds to come
// back. The drain's three quick tries (1.5 s and 4 s apart) all landed in those
// seconds, all failed, and it stopped 'offline' — with nothing to start it again,
// because iOS does not reliably say 'online' after a sleep. Now:
//
//   • A try that failed because the page slept does not count. The drain waits
//     until the page is awake and the device online, then tries that row again
//     (see pageWake.js for how a nap is known).
//   • A request that hung across a sleep gets 60 s after waking, not the rest
//     of its 3 minutes.
//   • A sync that starts with work to send records that it wants to finish
//     ('droppin.outbox.resume.v1'), and only a sync that ends without stopping
//     clears it. The sync strip reads it when the app comes back, comes online
//     or is reopened, and while it stands tries again on a back-off — so a sync
//     that stopped for ANY reason carries on by itself. A Sync now carries on
//     with what was queued when it was pressed (site mode keeps the rest held).
//   • The screen is kept on while a sync runs (Screen Wake Lock, iOS 18.4+), so
//     the iPad does not lock part-way through in the first place.
//   • The device database connection does not survive every sleep. One that is
//     found lost is opened again and the step tried once more; a fresh one is
//     opened after every nap; a transaction that ends without finishing is an
//     error, never a wait for ever.
//   • A change the server accepted is written down ('droppin.outbox.sent.v1')
//     before it leaves the queue. If the app is closed between the two, the next
//     sync finds the note and finishes that change WITHOUT sending it again: a
//     resent pin is a second pin, and its photos split between the two (the
//     Lakelands register, 10-21). If the queue will not let it go, the sync stops
//     and says so rather than carrying on past a change that is still queued.
//   • Reading the queue for a sync, or one row of it, no longer answers "empty"
//     when the device database fails: the sync stops ('device') and nothing is
//     skipped as though it were gone.
//
// ---------------------------------------------------------------------
// v0.255.0 — A PHOTO'S BYTES LIVE IN THEIR OWN DATABASE; A LISTING NEVER
// READS THEM.
//
// 2026-10-21. Brett, with 71 photos queued on the iPad: "the photos would keep
// stopping syncing when i push sync now it would get a few photos done then cut
// out … Droppin also kept closing", then at home on Wi-Fi: "it got to 3rd photo
// and the app crashed". Every row held its photo bytes inline (original,
// display and thumbnail copies), and listOutbox() was a getAll(): every
// listing — the drain's start, the id rewrite after each pin create, the sync
// strip's refresh on EVERY queue change, the plan's queued-photo view — pulled
// every photo of the queue into memory at once. 71 photos is several hundred
// MB per read, a few reads overlapping, and iOS ends the app for its memory.
//
//   • The bytes are now stored in a second database ('droppin-outbox-bytes',
//     one record per copy, `${bytesKey}/${copy}`); the row keeps `bytesKey`
//     and `blobMeta` ({ copy: { type, size } }) so it can still be counted
//     and sized. A send reads ONE row's bytes, at the moment of sending
//     (getRowBlobs), and nothing else ever holds them.
//   • A row queued before this release still holds its bytes inline. Both
//     shapes are read on every path. A background pass (splitInlineRows)
//     copies each inline row's bytes across, waits for that write to land,
//     and only then swaps the row for its light copy — the bytes are never in
//     neither place. Until a row is moved, a listing reads rows one at a time
//     and keeps only the light copy (lightRow), so it never holds two rows'
//     bytes at once.
//   • A row leaves only as before: removeFromOutbox, after a send the server
//     accepted or the user's own Discard. It takes its bytes with it.
//   • A crash in the instant between writing a new photo's bytes and adding
//     its row leaves bytes no row points at. They are left alone: storage,
//     not a lost photo, and nothing here deletes bytes on a guess.
//
// v0.174.0 — NOTHING WITH BYTES IN IT IS EVER DELETED BY THE APP.
//
// 2026-09-15, McDonald's Warnbro. Brett synced 19 items and their photos and
// got "synced, but 20 changes were refused" — and the photos were GONE. Two
// faults, both mine, both in this file:
//
//   1. PHOTO BYTES WERE STORED AS Blob OBJECTS. On iOS/iPadOS a Blob written
//      to IndexedDB is a REFERENCE to a file WebKit may reclaim; after the app
//      is backgrounded or restarted, reading it back throws. Bytes are now
//      stored as an ArrayBuffer ({ bytes, type }) — real bytes inside the
//      database, which survive a restart. Legacy Blob rows are still read.
//
//   2. A FAILURE DELETED THE WORK. A 4xx, and an unreadable blob, both called
//      removeFromOutbox() — so the one copy of a site photo was destroyed by
//      the very code meant to protect it. NOTHING IS DELETED NOW except by the
//      user's own explicit Discard. A row that cannot be sent is QUARANTINED:
//      it stays in the queue with its bytes, is listed in the sync strip, and
//      waits for Try again or Discard.
//
// The rule this file now follows: the app may refuse to send, may hold, may
// report — it may never throw away a change the user made.
//
// v0.171.0 — SITE MODE + PHOTOS AS BYTES + A DRAIN THAT DOES NOT GIVE UP.
//
//   • A row may carry `blobs` — { url, thumbnailUrl, displayUrl } as Blob
//     objects — instead of base64 strings in the body. IndexedDB stores a Blob
//     as bytes, so a 1 MB photo costs 1 MB, not 1.4 MB of base64 string held
//     in memory and JSON-serialised on every inspect. The blobs are turned
//     into data: URLs only at the moment of sending, so the API contract is
//     unchanged.
//   • drainOutbox() refuses to send in hold mode unless called with
//     { manual: true } (the Sync button / a positive Wi-Fi signal).
//   • One drain at a time. PlanScreen and the banner both call drain; two
//     concurrent drains replayed the same rows twice.
//   • Each send has a timeout and up to three attempts before the drain
//     stops; one hung 2 MB upload used to stall the whole queue forever.
//   • When a queued create replays, every row still pointing at it is
//     rewritten IN THE DATABASE with the real id, so a photo held back for a
//     later sync still finds its pin.
//   • Progress (n of total, what is going now) is published to subscribers.
//
// v0.118.0 — two problems, one file.
//
// VISIBILITY. The queue could only ever say "3 changes waiting to sync".
// It could not say WHICH three, when they were made, or what happened to
// them afterwards — a change the server rejected was dropped with a
// console.warn nobody reads. Now every replay is recorded in a small
// persisted log, rejections included, and the queue itself is listable
// in plain English (see outbox-labels.js).
//
// IDENTITY. The queue is per browser profile and carried no identity, so
// a change queued under one sign-in replayed under whatever sign-in came
// later. Rows are now stamped and the drain refuses any row that does not
// belong to the identity signed in right now (see outbox-identity.js).

import { getCurrentIdentityKey, classifyQueuedItem } from './outbox-identity.js';
import { describeQueuedChange } from './outbox-labels.js';
// 2026-08-20 — a queued change can point at a record queued but not yet
// created. See outbox-refs.js: 25 pins synced and every photo was lost because
// there was no defect id to attach them to yet.
import { resolveRefs, createdId } from './outbox-refs.js';
// v0.171.0 — SITE MODE. The drain asks before it sends: in 'hold' mode nothing
// leaves the phone until the user taps Sync (or the device positively reports
// Wi-Fi). See syncMode.js for the why.
import { isHoldMode } from './syncMode.js';
// v0.172.0 — a replayed row changes server data; cached lists are now stale.
import { markApiMutated } from './apiFreshness.js';
import { refusalMeansDone, mergeDrainResults } from './outbox-rules.js';
// v0.256.0 — a sleeping iPad, as the sync needs to know it.
import {
  onPageWake, napGeneration, sleptRecently, waitUntilAwake, waitForOnline,
  holdScreenAwake, releaseScreen,
} from './pageWake.js';

const DB_NAME    = 'droppin';
const DB_VERSION = 1;
const STORE      = 'outbox';

// Persisted sync history. localStorage, not IndexedDB: it is tiny,
// read synchronously by the banner on mount, and must survive a reload.
// Bodies are NEVER written here — only the derived label — so a queued
// photo's base64 payload can't blow the storage quota.
const LOG_KEY     = 'droppin.outbox.log.v1';
const LOG_MAX     = 25;
const DISMISS_KEY = 'droppin.outbox.logSeen.v1';

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  let p = null;
  p = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // v0.256.0 — a connection the browser closed is never handed out again.
      const drop = () => { if (_dbPromise === p) _dbPromise = null; };
      db.onclose = drop;
      db.onversionchange = () => { drop(); try { db.close(); } catch (_) {} };
      resolve(db);
    };
    req.onerror   = () => reject(req.error);
  }).catch((e) => {
    if (_dbPromise === p) _dbPromise = null;  // allow retry
    throw e;
  });
  _dbPromise = p;
  return p;
}

// ── A connection lost to a sleep (v0.256.0) ───────────────────────
//
// iOS can end the page's database connection while the iPad sleeps; the next
// transaction then throws (InvalidStateError, or UnknownError "Connection to
// Indexed Database server lost"). A fresh connection is opened after every nap,
// and a step that finds its connection lost opens another and runs once more.
// The old handle is not closed here: something may still be reading through it,
// and it is released once nothing holds it.
onPageWake(() => { _dbPromise = null; _bytesDbPromise = null; });

function lostConnection(e) {
  if (!e) return false;
  if (e.name === 'InvalidStateError' || e.name === 'UnknownError') return true;
  return /connection.*lost|closing|closed/i.test(String(e.message || ''));
}

async function withQueueDb(fn) {
  const p = openDb();
  try {
    return await fn(await p);
  } catch (e) {
    if (!lostConnection(e)) throw e;
    if (_dbPromise === p) _dbPromise = null;
    return fn(await openDb());
  }
}

async function withBytesDb(fn) {
  const p = openBytesDb();
  try {
    return await fn(await p);
  } catch (e) {
    if (!lostConnection(e)) throw e;
    if (_bytesDbPromise === p) _bytesDbPromise = null;
    return fn(await openBytesDb());
  }
}

// A transaction that ends without finishing — the connection closed under it —
// fires only 'abort'. Waiting on 'complete' and 'error' alone would wait for ever,
// and the sync with it.
function abortError(tx) {
  return tx.error || Object.assign(new Error('the device database closed the transaction'), { name: 'InvalidStateError' });
}

// A failed request fires 'error' BEFORE the transaction aborts, so tx.error is
// still null then: the request's own error is the reason (a bare null would
// hide a lost connection from withQueueDb's one retry).
function txError(tx, e) {
  return (e && e.target && e.target.error) || abortError(tx);
}

// ── Photo bytes database (v0.255.0) ───────────────────────────────
//
// Separate from 'droppin' so the queue's own database never changes version
// (an open tab on the old build would block the upgrade). One record per copy
// of a photo, key `${bytesKey}/${copy}`, value { bytes, type }.
const BYTES_DB      = 'droppin-outbox-bytes';
const BYTES_VERSION = 1;
const BYTES_STORE   = 'bytes';

let _bytesDbPromise = null;

function openBytesDb() {
  if (_bytesDbPromise) return _bytesDbPromise;
  let p = null;
  p = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(BYTES_DB, BYTES_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(BYTES_STORE)) db.createObjectStore(BYTES_STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      // v0.256.0 — as openDb: a closed connection is never handed out again.
      const drop = () => { if (_bytesDbPromise === p) _bytesDbPromise = null; };
      db.onclose = drop;
      db.onversionchange = () => { drop(); try { db.close(); } catch (_) {} };
      resolve(db);
    };
    req.onerror   = () => reject(req.error);
  }).catch((e) => {
    if (_bytesDbPromise === p) _bytesDbPromise = null;  // allow retry
    throw e;
  });
  _bytesDbPromise = p;
  return p;
}

// Writes every copy under `base` and resolves only once the write has landed,
// with the copies' { type, size } for the row. Throws on any failure: a caller
// must never point a row at bytes that are not there.
async function putBytes(base, blobs) {
  const meta = blobMetaOf(blobs);
  await withBytesDb((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(BYTES_STORE, 'readwrite');
    const store = tx.objectStore(BYTES_STORE);
    for (const [key, stored] of Object.entries(blobs)) {
      if (!stored) continue;
      store.put({ bytes: stored.bytes, type: stored.type || 'image/jpeg' }, `${base}/${key}`);
    }
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject(txError(tx, e));
    tx.onabort    = () => reject(abortError(tx));
  }));
  return meta;
}

// { copy: { bytes, type } } for the copies asked for; a copy not there is
// simply absent (the drain then quarantines the row as unreadable).
async function getBytes(base, keys) {
  return withBytesDb((db) => new Promise((resolve, reject) => {
    const out = {};
    const tx = db.transaction(BYTES_STORE, 'readonly');
    const store = tx.objectStore(BYTES_STORE);
    for (const key of keys) {
      const req = store.get(`${base}/${key}`);
      req.onsuccess = () => { if (isStoredBytes(req.result)) out[key] = req.result; };
    }
    tx.oncomplete = () => resolve(out);
    tx.onerror    = (e) => reject(txError(tx, e));
    tx.onabort    = () => reject(abortError(tx));
  }));
}

// Every copy under `base`. Only called once the row that owned them is gone.
async function deleteBytes(base) {
  try {
    await withBytesDb((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(BYTES_STORE, 'readwrite');
      tx.objectStore(BYTES_STORE).delete(IDBKeyRange.bound(`${base}/`, `${base}/￿`));
      tx.oncomplete = () => resolve();
      tx.onerror    = (e) => reject(txError(tx, e));
      tx.onabort    = () => reject(abortError(tx));
    }));
  } catch (e) {
    console.warn('[outbox] could not clear a sent photo\'s bytes:', e);
  }
}

function blobMetaOf(blobs) {
  const meta = {};
  for (const [key, v] of Object.entries(blobs || {})) {
    if (!v) continue;
    meta[key] = {
      type: v.type || 'image/jpeg',
      size: isStoredBytes(v) ? v.bytes.byteLength : (Number(v.size) || 0),
    };
  }
  return meta;
}

// The row as everything outside the drain sees it: inline bytes (a row queued
// before v0.255.0, not yet moved) replaced by their sizes. No reference to the
// bytes is kept, so a listing holds at most the row it is reading.
function lightRow(row) {
  if (!row || !row.blobs) return row;
  const { blobs, ...rest } = row;
  return { ...rest, blobMeta: blobMetaOf(blobs) };
}

function holdsInlineBytes(row) {
  if (!row || !row.blobs || typeof row.blobs !== 'object') return false;
  const vals = Object.values(row.blobs).filter(Boolean);
  return vals.length > 0 && vals.every(isStoredBytes);
}

// ── Moving old rows' bytes across (v0.255.0) ──────────────────────
//
// Once per page load, in the background. For each row still holding its bytes
// inline: copy them to the bytes database under `row-<id>`, wait for that write
// to land, then — in ONE queue transaction, against the row as it is NOW — swap
// it for its light copy. A row removed meanwhile has its copy cleared. A row
// still holding pre-v0.174 Blob handles is left exactly as it is (reading those
// is what failed on the iPad; the drain still tries them).
//
// Not data migration on load in the sense CLAUDE.md forbids: every path reads
// both shapes, and the pass only makes the old shape rarer.
let _splitRun    = null;
let _splitDone   = false;   // no inline-bytes rows left: listings can getAll()
let _splitFailed = false;   // the bytes database refused a write: stop trying
let _inlineAdds  = 0;       // photos queued inline (bytes database refused them)

function startSplit() {
  if (_splitDone || _splitFailed || _splitRun) return;
  _splitRun = splitInlineRows()
    .catch((e) => {
      // v0.256.0 — a connection lost to a nap is not the bytes database
      // refusing: the next listing starts the pass again on a fresh one.
      if (!lostConnection(e)) _splitFailed = true;
      console.warn('[outbox] moving photo bytes failed:', e);
    })
    .finally(() => { _splitRun = null; });
}

async function splitInlineRows() {
  const db = await openDb();
  const addsAtStart = _inlineAdds;
  let leftInline = false;
  for (const id of await getAllRowKeys(db)) {
    const row = await getRawRow(db, id);
    if (!holdsInlineBytes(row)) continue;
    const base = `row-${id}`;
    try {
      await putBytes(base, row.blobs);
    } catch (e) {
      if (lostConnection(e)) throw e;
      _splitFailed = true;
      console.warn('[outbox] bytes database would not take a photo; leaving the rest inline:', e);
      return;
    }
    const sizes = blobMetaOf(row.blobs);
    const outcome = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      let result = 'left';
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const now = getReq.result;
        if (!now) { result = 'gone'; return; }
        // Already moved (another open window ran the same pass): it points at
        // this very copy, which must stay.
        if (!holdsInlineBytes(now)) { result = now.bytesKey === base ? 'moved' : 'stale'; return; }
        // Bytes never change after queueing; if these somehow did, leave the
        // row inline rather than point it at a copy that is not its own.
        const nowSizes = blobMetaOf(now.blobs);
        const same = Object.keys(nowSizes).length === Object.keys(sizes).length
          && Object.entries(nowSizes).every(([k, m]) => sizes[k] && sizes[k].size === m.size);
        if (!same) return;
        const { blobs, ...rest } = now;
        store.put({ ...rest, bytesKey: base, blobMeta: nowSizes });
        result = 'moved';
      };
      tx.oncomplete = () => resolve(result);
      tx.onerror    = (e) => reject(txError(tx, e));
      tx.onabort    = () => reject(abortError(tx));
    });
    // Not moved: no row points at the copy just made (only this pass writes
    // `row-` keys), so it goes. The row's own bytes are untouched.
    if (outcome !== 'moved') await deleteBytes(base);
    if (outcome === 'left') leftInline = true;
  }
  // Only once nothing is left inline may a listing read every row at once.
  if (!leftInline && _inlineAdds === addsAtStart) _splitDone = true;
}

function getAllRowKeys(db) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAllKeys();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
    tx.onabort    = () => reject(abortError(tx));
  });
}

function getRawRow(db, id) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror   = () => reject(req.error);
    tx.onabort    = () => reject(abortError(tx));
  });
}

// ── Pub-sub for queue changes ────────────────────────────────────
const _listeners = new Set();
// v0.171.0 — listeners receive an event: { type: 'change' } (default),
// { type: 'progress' } or { type: 'applied', method, path, localId, response }.
// Existing listeners that ignore the argument keep working.
function notify(evt) {
  const e = evt || { type: 'change' };
  _listeners.forEach((fn) => { try { fn(e); } catch {} });
}
export function subscribeOutbox(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// ── Add ───────────────────────────────────────────────────────────
//
// v0.118.0 — stamps the identity signed in AT THE MOMENT OF QUEUEING.
// If nobody is identifiably signed in the stamp is null, and the row is
// treated as unstamped on drain (shown, never auto-sent).
//
// v0.171.0 — `extras.blobs` = { url, thumbnailUrl, displayUrl } as Blobs. Stored
// beside the body, never inside it; merged in as data: URLs at send time.
export async function addToOutbox(method, path, body, extras) {
  try {
    await openDb();  // no database, no queue: say so before reading any photo
    const identityKey = getCurrentIdentityKey();
    // v0.174.0 — bytes, not Blob references. See the header note: a Blob in
    // IndexedDB does not survive an iOS app restart.
    const blobs = await storableBlobs(extras && extras.blobs);
    // v0.255.0 — the bytes go to their own database first, and the row only
    // points at them once that write has landed. If that database will not
    // take them, they stay inline as before (and the move pass tries again
    // later): the photo is kept either way.
    let photo = blobs ? { blobs } : {};
    let bytesBase = null;
    if (blobs) {
      const base = `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      try {
        const blobMeta = await putBytes(base, blobs);
        photo = { bytesKey: base, blobMeta };
        bytesBase = base;
      } catch (e) {
        console.warn('[outbox] bytes database would not take this photo; keeping it in the row:', e);
        _inlineAdds++;
        _splitDone = false;
      }
    }
    let id;
    const queuedAt = Date.now();
    try {
      // v0.256.0 — an aborted transaction wrote nothing, so a retry on a fresh
      // connection cannot queue the change twice.
      id = await withQueueDb((db) => new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const req = tx.objectStore(STORE).add({
          method, path, body, queuedAt, identityKey, ...photo,
        });
        // Queued once the write has landed, not when the add was accepted.
        let newId = null;
        req.onsuccess = () => { newId = req.result; };
        req.onerror   = () => reject(req.error);
        tx.oncomplete = () => resolve(newId);
        tx.onabort    = () => reject(abortError(tx));
      }));
    } catch (e) {
      // Not queued (the caller is told null), so these bytes belong to nothing.
      if (bytesBase) await deleteBytes(bytesBase);
      throw e;
    }
    notify();
    return id;
  } catch (e) {
    console.warn('[outbox] add failed:', e);
    return null;
  }
}

// ── Photo bytes ───────────────────────────────────────────────────
//
// v0.174.0 — { url: Blob, thumbnailUrl: Blob|null, displayUrl: Blob|null }
// becomes { url: { bytes: ArrayBuffer, type }, … } for storage. An ArrayBuffer
// is structured-cloned into IndexedDB as actual bytes; a Blob is stored as a
// handle to a file the OS may reclaim, which is how 20 site photos became
// unreadable on an iPad. Returns undefined when there is nothing to store, so
// a row without photos is stored exactly as before.
async function storableBlobs(blobs) {
  if (!blobs || typeof blobs !== 'object') return undefined;
  const out = {};
  let any = false;
  for (const [key, blob] of Object.entries(blobs)) {
    if (!blob) continue;
    const stored = await blobToBytes(blob);
    if (stored) { out[key] = stored; any = true; }
  }
  return any ? out : undefined;
}

async function blobToBytes(blob) {
  const type = (blob && blob.type) || 'image/jpeg';
  try {
    if (typeof blob.arrayBuffer === 'function') return { bytes: await blob.arrayBuffer(), type };
    const bytes = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload  = () => resolve(r.result);
      r.onerror = () => reject(r.error || new Error('blob read failed'));
      r.readAsArrayBuffer(blob);
    });
    return { bytes, type };
  } catch (e) {
    console.warn('[outbox] could not read photo bytes at queue time:', e);
    return null;
  }
}

// { bytes, type } → a data: URL, without FileReader (which is what failed on
// the iPad). Chunked so a megabyte does not blow the argument limit.
export function bytesToDataUrl(stored) {
  const u8 = new Uint8Array(stored.bytes);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < u8.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, i + CHUNK));
  }
  return `data:${stored.type || 'image/jpeg'};base64,${btoa(binary)}`;
}

export function isStoredBytes(v) {
  return !!v && typeof v === 'object' && v.bytes != null && typeof v.bytes.byteLength === 'number';
}

// ── Quarantine ────────────────────────────────────────────────────
//
// v0.174.0 — a row the server refused, or whose bytes could not be read, is
// MARKED, never removed. It stops being retried automatically (so it cannot
// spin), stays listed in the sync strip, and keeps its bytes until the user
// says Discard.
async function quarantine(id, reason, message, status) {
  await updateOutboxItem(id, (row) => ({
    ...row,
    quarantinedAt: Date.now(),
    quarantineReason: reason,          // 'refused' | 'unreadable'
    quarantineMessage: message || null,
    quarantineStatus: status || null,
  }));
}

export function isQuarantined(item) {
  return !!(item && item.quarantinedAt);
}

// "Try these again" — clears the marks so the next drain sends them. Used by
// the sync strip after the user has read what went wrong.
export async function retryQuarantined(ids) {
  const list = Array.isArray(ids) ? ids : null;
  const items = await listOutbox();
  let n = 0;
  for (const item of items) {
    if (!isQuarantined(item)) continue;
    if (list && !list.some(i => Number(i) === Number(item.id))) continue;
    await updateOutboxItem(item.id, (row) => {
      const next = { ...row };
      delete next.quarantinedAt;
      delete next.quarantineReason;
      delete next.quarantineMessage;
      delete next.quarantineStatus;
      return next;
    });
    n++;
  }
  return n;
}

// ── List ──────────────────────────────────────────────────────────
//
// v0.255.0 — every row LIGHT: no photo bytes, ever (see the header). Once no
// row holds its bytes inline a getAll() is cheap; until then the rows are read
// one at a time and each is stripped before the next is read.
export async function listOutbox() {
  try {
    return await listOutboxRows();
  } catch {
    return [];
  }
}

// v0.256.0 — the same listing, but a listing that FAILED throws instead of
// answering "nothing queued". The drain reads the queue through this: an empty
// answer from a database that did not open must not look like a finished sync.
async function listOutboxRows() {
  return withQueueDb(async (db) => {
    startSplit();
    if (_splitDone) {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).getAll();
        req.onsuccess = () => resolve((req.result || []).map(lightRow));
        req.onerror   = () => reject(req.error);
        tx.onabort    = () => reject(abortError(tx));
      });
    }
    // A transaction of its own per row: a request kept alive by a long
    // transaction keeps the row it read, bytes and all.
    const rows = [];
    for (const id of await getAllRowKeys(db)) {
      const row = await getRawRow(db, id);
      if (row) rows.push(lightRow(row));
    }
    return rows;
  });
}

// v0.255.0 — one row's photo bytes, read now, for the row as it is now:
// { copy: { bytes, type } } (or a pre-v0.174 Blob), only the copies asked for.
// Null when the row is gone. Throws when the bytes database cannot be read, so
// a sender can tell "not readable" from "no photo".
// v0.256.0 — and throws when the ROW cannot be read: a queue that did not answer
// is not a row that is gone.
export async function getRowBlobs(id, keys) {
  const row = await withQueueDb((db) => getRawRow(db, Number(id)));
  if (!row) return null;
  if (row.bytesKey) {
    const want = keys || Object.keys(row.blobMeta || {});
    return getBytes(row.bytesKey, want);
  }
  const inline = row.blobs || {};
  const out = {};
  for (const key of keys || Object.keys(inline)) {
    if (inline[key]) out[key] = inline[key];
  }
  return out;
}

// ── Get / update one row ──────────────────────────────────────────
//
// v0.171.0 — editing a pin that only exists in the queue edits its queued
// create in place (see outbox-local.js mergeQueuedPatch). `updater` receives
// the stored row and returns the row to store; returning null leaves it.
export async function getOutboxItem(id) {
  try {
    return await withQueueDb((db) => getRawRow(db, Number(id)));
  } catch {
    return null;
  }
}

export async function updateOutboxItem(id, updater) {
  try {
    const changed = await updateOutboxRow(id, updater);
    if (changed) notify();
    return changed;
  } catch (e) {
    console.warn('[outbox] update failed:', e);
    return false;
  }
}

// v0.256.0 — the same update, but one that FAILED throws instead of answering
// "nothing to change", for the drain's id rewrite (a pin's photos must not be
// left pointing at an id that no longer exists). Resolves once the write has
// landed. No notify: the caller says when the queue changed.
async function updateOutboxRow(id, updater) {
  return withQueueDb((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    let changed = false;
    const getReq = store.get(Number(id));
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (!row) return;
      let next = null;
      try { next = updater(row); } catch (e) { reject(e); try { tx.abort(); } catch (_) {} return; }
      if (!next) return;
      // v0.255.0 — callers build `next` from rows that may have come from a
      // listing (light, no bytes). Whatever holds or points at this row's
      // photo is carried over, so an edit can never drop its bytes.
      const keep = {};
      for (const k of ['blobs', 'bytesKey', 'blobMeta']) {
        if (row[k] !== undefined && next[k] === undefined) keep[k] = row[k];
      }
      if (next.bytesKey && keep.blobs) delete keep.blobs;
      const putReq = store.put({ ...next, ...keep, id: row.id });
      putReq.onsuccess = () => { changed = true; };
      putReq.onerror   = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
    tx.oncomplete  = () => resolve(changed);
    tx.onabort     = () => reject(abortError(tx));
  }));
}

// ── Remove ────────────────────────────────────────────────────────
//
// v0.255.0 — the row first, then its bytes: never bytes gone from a row that
// is still queued. `row-<id>` is cleared too (a move pass may have copied this
// row's bytes there an instant before it went; ids are never reused).
// v0.256.0 — answers whether the row is gone. False = the device database did
// not let it go, and the row is still queued: the drain stops there rather than
// carry on as though it had been sent and removed.
export async function removeFromOutbox(id) {
  try {
    const bytesKey = await withQueueDb((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      let key = null;
      const getReq = store.get(Number(id));
      getReq.onsuccess = () => {
        key = (getReq.result && getReq.result.bytesKey) || null;
        store.delete(Number(id));
      };
      tx.oncomplete = () => resolve(key);
      tx.onerror    = (e) => reject(txError(tx, e));
      tx.onabort    = () => reject(abortError(tx));
    }));
    if (bytesKey) await deleteBytes(bytesKey);
    if (bytesKey !== `row-${id}`) await deleteBytes(`row-${id}`);
    notify();
    return true;
  } catch (e) {
    console.warn('[outbox] remove failed:', e);
    return false;
  }
}

// ── Count ─────────────────────────────────────────────────────────
export async function countOutbox() {
  try {
    return await withQueueDb((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).count();
      req.onsuccess = () => resolve(req.result || 0);
      req.onerror   = () => reject(req.error);
      tx.onabort    = () => reject(abortError(tx));
    }));
  } catch {
    return 0;
  }
}

// ── Inspect ───────────────────────────────────────────────────────
//
// v0.118.0 — the queue, split by whether it may be replayed, each row
// already carrying its human label. This is what the banner lists.
//
//   pending  rows stamped with the CURRENT identity, waiting to send.
//   blocked  rows that will never be sent automatically — either
//            stamped with a different sign-in ('mismatch'), queued
//            before stamping existed ('unstamped'), or unjudgeable
//            because nobody is signed in ('no-session').
//
// A blocked row's identity stamp is NOT returned. Telling the current
// user "these were made by a different sign-in" is the honest message;
// naming that other person would leak them to whoever is holding the
// device now, which is the same failure in the other direction.
export async function inspectOutbox() {
  const currentKey = getCurrentIdentityKey();
  const items = await listOutbox();
  const pending = [];
  const blocked = [];
  for (const item of items) {
    // v0.174.0 — a quarantined row is reported as what it is, whatever its
    // identity verdict: it is waiting on the user, not on reception.
    const verdict = isQuarantined(item)
      ? (item.quarantineReason === 'unreadable' ? 'unreadable' : 'refused')
      : classifyQueuedItem(item, currentKey);
    const { title, detail } = describeQueuedChange(item);
    const row = {
      id: item.id,
      method: item.method,
      path: item.path,
      queuedAt: Number.isFinite(Number(item.queuedAt)) ? Number(item.queuedAt) : null,
      title,
      detail,
      verdict,
      // v0.171.0 — so the banner can say "3 pins, 40 photos (52 MB)" instead
      // of "43 changes".
      kind: rowKind(item),
      bytes: rowBytes(item),
      message: item.quarantineMessage || null,
      status: item.quarantineStatus || null,
    };
    if (verdict === 'match') pending.push(row);
    else blocked.push(row);
  }
  return { pending, blocked };
}

// ── Discard / adopt ───────────────────────────────────────────────
//
// Explicit user actions on blocked rows. Both are deliberate clicks on a
// list the user has just read — never automatic.
export async function discardOutboxItems(ids) {
  const list = Array.isArray(ids) ? ids : [ids];
  for (const id of list) await removeFromOutbox(id);
}

// "These are mine — send them." Restamps UNSTAMPED rows only (legacy
// rows from before v0.118.0). A row stamped with a DIFFERENT identity is
// deliberately not adoptable: we know it isn't theirs, and an "apply
// anyway" on a known cross-identity write is the exact thing that must
// never be possible. Returns how many rows were adopted.
export async function adoptUnstampedItems() {
  const currentKey = getCurrentIdentityKey();
  if (!currentKey) return 0;
  let adopted = 0;
  try {
    const items = await listOutbox();
    for (const item of items) {
      if (classifyQueuedItem(item, currentKey) !== 'unstamped') continue;
      // v0.255.0 — restamped against the stored row: a listed row is light, and
      // putting it back whole would drop its photo.
      const ok = await updateOutboxItem(item.id, (row) => (
        classifyQueuedItem(row, currentKey) === 'unstamped' ? { ...row, identityKey: currentKey } : null
      ));
      if (ok) adopted++;
    }
  } catch (e) {
    console.warn('[outbox] adopt failed:', e);
  }
  if (adopted) notify();
  return adopted;
}

// ── Sync log ──────────────────────────────────────────────────────
//
// What actually happened, last N replays, newest first. Persisted so it
// survives the reload that used to erase every trace of a sync.
//
//   entry = { at, title, detail, outcome, message }
//   outcome ∈ 'applied' | 'rejected' | 'blocked'
export function getSyncLog() {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_e) {
    return [];
  }
}

export function clearSyncLog() {
  try { localStorage.removeItem(LOG_KEY); } catch (_e) {}
  try { localStorage.removeItem(DISMISS_KEY); } catch (_e) {}
  notify();
}

function appendSyncLog(entries) {
  if (!entries || !entries.length) return;
  try {
    const next = [...entries, ...getSyncLog()].slice(0, LOG_MAX);
    localStorage.setItem(LOG_KEY, JSON.stringify(next));
    // No need to touch the seen marker: these entries are newer than it,
    // so they read as unseen while everything already acknowledged stays
    // acknowledged.
  } catch (_e) { /* quota / private mode — the drain still worked */ }
}

// The banner uses these to stay quiet about a result the user has
// already looked at, instead of re-announcing it on every reload, and to
// summarise only the LATEST sync rather than the whole history.
//
// The marker is the timestamp of the newest entry the user has seen;
// anything newer is unseen.
export function getSyncSeenAt() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch (_e) {
    return 0;
  }
}

export function markSyncLogSeen() {
  const top = getSyncLog()[0];
  const at = Number(top?.at);
  try {
    localStorage.setItem(DISMISS_KEY, String(Number.isFinite(at) && at > 0 ? at : Date.now()));
  } catch (_e) {}
  notify();
}

export function syncLogHasUnseen() {
  const top = getSyncLog()[0];
  const at = Number(top?.at);
  if (!Number.isFinite(at) || at <= 0) return false;
  return at > getSyncSeenAt();
}

// ── Drain ─────────────────────────────────────────────────────────
//
// Replays queued requests in FIFO. For each:
//   - IDENTITY FIRST (v0.118.0). A row that isn't stamped with the
//     identity signed in right now is SKIPPED, left in the queue and
//     reported back. It is never sent "to be helpful". Skipping does
//     not abort the drain — one quarantined row must not hold up the
//     current user's own work behind it.
//   - On 2xx response: remove from queue, record as applied.
//   - On 4xx (server says NO): remove from queue — server-side
//     validation rejected it; replaying won't help, the only way to
//     recover is the user re-doing the action with valid data. v0.118.0
//     records it in the sync log so the USER sees it, not just the
//     console. A change that vanished silently is exactly the thing
//     this release exists to stop.
//   - On 5xx: leave in queue, abort the drain (avoids hammering a
//     flaky server). Next online tick will retry.
//   - On network fail / timeout (v0.171.0): retry the SAME row up to
//     SEND_ATTEMPTS times with a short back-off, then leave it in the
//     queue and stop. A queue of forty photos used to die on the first
//     dropped packet and start again from scratch on the next 'online'
//     event, which on site meant it never finished.
//
// `fetcher` lets callers inject a custom fetch (used by tests).
// `opts.identityKey` lets tests pin the identity instead of reading
// session storage. Defaults to globalThis.fetch / the live session.
// `opts.manual` (v0.171.0) — the user asked for this sync (or the device
// reported Wi-Fi); overrides hold mode. Without it, hold mode returns
// { held: true } and sends nothing.
//
// Returns { drained, remaining, pending, blocked, applied, rejected,
//           blockedItems, stopped, held }.
// `remaining` keeps its old meaning (every row still in the queue) so
// existing callers are unaffected; `pending` is the subset that is
// actually this user's and still to go.

const SEND_ATTEMPTS      = 3;
const RETRY_DELAYS_MS    = [1500, 4000];
const TIMEOUT_PHOTO_MS   = 180000;   // a 2 MB photo on a poor link
const TIMEOUT_OTHER_MS   = 45000;
// v0.256.0 — see the header: a send that failed because the iPad slept is not
// one of the three tries, up to this many times per row; and a request still
// out when the iPad wakes gets this long to answer.
const SLEEP_EXCUSES      = 5;
const AFTER_WAKE_MS      = 60000;
const SETTLE_AFTER_WAKE_MS = 3000;

// ── A sync to carry on with (v0.256.0) ────────────────────────────
//
// Written when a sync starts with something to send, cleared when one runs to
// the end. While it is there, the banner starts the sync again by itself when
// the iPad wakes, when the device says it is online, when the app is opened
// again, and on a back-off while it waits. `manual` + `upToId`: a Sync now
// pressed in site mode carries on with what was queued when it was pressed,
// and nothing queued after.
const RESUME_KEY = 'droppin.outbox.resume.v1';

export function getSyncResume() {
  try {
    const v = JSON.parse(localStorage.getItem(RESUME_KEY) || 'null');
    return v && typeof v === 'object' ? v : null;
  } catch (_e) {
    return null;
  }
}

export function clearSyncResume() {
  let had = false;
  try { had = localStorage.getItem(RESUME_KEY) != null; localStorage.removeItem(RESUME_KEY); } catch (_e) {}
  if (had) notify();
}

function setSyncResume(v) {
  try { localStorage.setItem(RESUME_KEY, JSON.stringify(v)); } catch (_e) {}
}

// ── What the server already took (v0.256.0) ──────────────────────
//
// A row the server accepted is written here BEFORE it leaves the queue. If it
// cannot leave (the device database lost its connection in a nap, or the app
// was closed in between), the next sync finds it here and finishes it without
// sending it again: no second pin on the plan with the photos split between the
// two (Lakelands). Cleared once the row is gone. Kept: the server's answer when
// it is small (the plan needs the new pin's id), else only the id.
// It does not cover the app closing between the server applying a change and
// its answer arriving; only the server can (a key sent with each change).
const SENT_KEY = 'droppin.outbox.sent.v1';
const SENT_RESPONSE_MAX = 16384;

function sentKeyOf(item) {
  return `${item.id}:${item.queuedAt ?? ''}`;
}

function readSentAll() {
  try {
    const v = JSON.parse(localStorage.getItem(SENT_KEY) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch (_e) {
    return {};
  }
}

function writeSentAll(all) {
  try {
    if (Object.keys(all).length) localStorage.setItem(SENT_KEY, JSON.stringify(all));
    else localStorage.removeItem(SENT_KEY);
  } catch (_e) { /* quota / private mode: the row is removed straight after anyway */ }
}

function readSent(item) {
  const hit = readSentAll()[sentKeyOf(item)];
  return hit && typeof hit === 'object' ? hit : null;
}

function recordSent(item, entry) {
  let response = entry.response ?? null;
  try { if (response != null && JSON.stringify(response).length > SENT_RESPONSE_MAX) response = null; } catch (_e) { response = null; }
  const all = readSentAll();
  all[sentKeyOf(item)] = { status: entry.status, response, created: entry.created ?? null, at: Date.now() };
  writeSentAll(all);
}

function clearSent(item) {
  const all = readSentAll();
  const key = sentKeyOf(item);
  if (!(key in all)) return;
  delete all[key];
  writeSentAll(all);
}

// Entries whose row is no longer queued (discarded, or removed while the entry
// failed to clear) go; nothing else can ever read them.
function pruneSent(rows) {
  const all = readSentAll();
  const live = new Set(rows.map(sentKeyOf));
  let changed = false;
  for (const key of Object.keys(all)) {
    if (!live.has(key)) { delete all[key]; changed = true; }
  }
  if (changed) writeSentAll(all);
}

// v0.171.0 — one drain at a time, module-wide. A second caller while one is
// running gets the same promise, so the queue is never replayed twice.
let _drainInFlight = null;

// Live progress for the banner / plan screen. `active` false when idle.
let _progress = { active: false, done: 0, total: 0, current: null, startedAt: 0, kind: null };
export function getDrainProgress() { return _progress; }
function setProgress(patch) {
  _progress = { ..._progress, ...patch };
  notify({ type: 'progress', progress: _progress });
}

const wait = (ms) => new Promise((res) => setTimeout(res, ms));

// 2026-09-15 — ASKED AGAIN WHILE RUNNING, IT RUNS AGAIN. A drain reads the queue
// once, at the start. A pin saved while an earlier save's photos were still
// uploading queued its change behind that read, so a removed photo's DELETE sat
// unsent until something else started a drain, and the photo stayed on the pin
// (Brett: "Its like i need to do it twice before it gets removed"). A call that
// arrives mid-drain now asks for one more pass as soon as this one ends — unless
// this one stopped (offline, a server error), or was held by site mode and the
// new call is not a manual Sync now.
let _drainAgain = null;

export function drainOutbox(fetcher, opts) {
  if (_drainInFlight) { _drainAgain = { fetcher, opts }; return _drainInFlight; }
  _drainInFlight = (async () => {
    let result = await drainOnce(fetcher, opts);
    while (_drainAgain && result && !result.stopped
      && (!result.held || !!(_drainAgain.opts && _drainAgain.opts.manual))) {
      const again = _drainAgain;
      _drainAgain = null;
      result = mergeDrainResults(result, await drainOnce(again.fetcher, again.opts));
    }
    return result;
  })().finally(() => { _drainInFlight = null; _drainAgain = null; });
  return _drainInFlight;
}

export function isDraining() { return !!_drainInFlight; }

async function drainOnce(fetcher, opts) {
  const f = fetcher || ((path, opts2) => fetch(path, opts2));
  const manual = !!(opts && opts.manual);
  const holdMode = opts && 'holdMode' in opts ? !!opts.holdMode : isHoldMode();
  const currentKey = opts && 'identityKey' in opts ? opts.identityKey : getCurrentIdentityKey();

  if (holdMode && !manual) {
    const remaining = await countOutbox();
    return {
      drained: 0, remaining, pending: remaining, blocked: 0,
      applied: [], rejected: [], blockedItems: [], stopped: null, held: true,
    };
  }

  // v0.256.0 — a queue that did not answer is not an empty queue.
  let listed;
  try {
    listed = await listOutboxRows();
  } catch (e) {
    console.warn('[outbox] the queue on this device did not open; stopping:', e);
    const remaining = await countOutbox();
    return {
      drained: 0, remaining, pending: remaining, blocked: 0,
      applied: [], rejected: [], blockedItems: [], stopped: 'device', held: false,
    };
  }
  pruneSent(listed);
  const upToId = manual && opts && opts.upToId != null ? Number(opts.upToId) : null;
  const items = upToId != null ? listed.filter(it => Number(it.id) <= upToId) : listed;

  let drained = 0;
  let stopped = null;
  // localId → the id the server gave that queued record, so anything queued
  // AFTER it can be rewritten to point at the real row.
  const idMap = new Map();
  const applied = [];
  const rejected = [];
  const blockedItems = [];
  const logEntries = [];

  const sendable = items.filter(it => !isQuarantined(it) && classifyQueuedItem(it, currentKey) === 'match').length;
  setProgress({ active: true, done: 0, total: sendable, current: null, startedAt: Date.now(), kind: null });

  // v0.256.0 — from here until it runs to the end, this sync is carried on after
  // a nap, and the screen is kept on while it runs.
  const holding = sendable > 0;
  if (holding) {
    const lastId = items.reduce((m, it) => Math.max(m, Number(it.id) || 0), 0);
    setSyncResume({ at: Date.now(), manual, upToId: manual ? (upToId ?? lastId) : null });
    holdScreenAwake();
  }

  try {
    for (const item of items) {
      const { title, detail } = describeQueuedChange(item);

      // v0.174.0 — quarantined: held for the user, never retried on its own
      // and NEVER deleted. Skipping it does not hold up anything behind it.
      if (isQuarantined(item)) {
        blockedItems.push({
          id: item.id, title, detail,
          verdict: item.quarantineReason === 'unreadable' ? 'unreadable' : 'refused',
          queuedAt: item.queuedAt ?? null,
          message: item.quarantineMessage || null,
        });
        continue;
      }

      const verdict = classifyQueuedItem(item, currentKey);

      if (verdict !== 'match') {
        // FAIL CLOSED. Not ours (or not provably ours) → do not send.
        blockedItems.push({ id: item.id, title, detail, verdict, queuedAt: item.queuedAt ?? null });
        continue;
      }

      // v0.256.0 — the server already took this one; a sync stopped before it
      // could leave the queue. Finish it: never send it a second time.
      const sent = readSent(item);
      let res = null;
      if (sent) {
        res = { ok: true, status: sent.status };
      } else {
        // Point this change at the real row, if it was queued against one that did
        // not exist yet. An unresolved reference is HELD, never sent — a photo
        // posted against a made-up defect id would either be rejected and dropped,
        // or land on an unrelated defect.
        const { body: refBody, unresolved } = resolveRefs(item.body, idMap);
        if (unresolved.length) {
          blockedItems.push({
            id: item.id, title, detail, verdict: 'awaiting-parent',
            queuedAt: item.queuedAt ?? null,
          });
          continue;
        }

        setProgress({ current: { id: item.id, title, detail }, kind: rowKind(item) });

        // v0.171.0 — bytes leave the phone as data: URLs, built only now.
        // v0.255.0 — read from this ONE row now, never carried in the listing.
        let sendBody = refBody;
        let rowBlobs;
        try {
          rowBlobs = await getRowBlobs(item.id);
        } catch (e) {
          // The photo store would not open or read. The bytes are very likely
          // still there, so nothing is marked: stop, and the next Sync now
          // starts again from this row.
          console.warn(`[outbox] photo store unreadable at row ${item.id}; stopping:`, e);
          stopped = 'device';
          break;
        }
        if (rowBlobs === null) continue;   // discarded while the sync ran
        try {
          sendBody = await withBlobsAsDataUrls(refBody, rowBlobs);
        } catch (e) {
          // v0.174.0 — THE ROW STAYS. This path used to delete it, which is how
          // a site photo could vanish: on iOS a Blob stored in IndexedDB can
          // become unreadable after a restart, and the app then destroyed the
          // only copy. It is quarantined and listed instead; the bytes may yet
          // be recoverable, and even if they are not, the user finds out from a
          // list of what is missing rather than from an empty report.
          const message = 'This photo could not be read back from the device.';
          console.warn(`[outbox] photo bytes unreadable for ${item.path}:`, e);
          await quarantine(item.id, 'unreadable', message, 0);
          blockedItems.push({ id: item.id, title, detail, verdict: 'unreadable', queuedAt: item.queuedAt ?? null, message });
          logEntries.push({ at: Date.now(), title, detail, outcome: 'blocked', message });
          continue;
        }

        // v0.174.0 — a photo row that would go up with no image is stopped HERE,
        // with an honest reason, rather than sent for the server to answer 400
        // to (which is what "20 changes were refused" looked like on site).
        if (rowKind(item) === 'photo' && !(sendBody && sendBody.url)) {
          const message = 'This photo had no image data left on the device.';
          console.warn(`[outbox] photo row ${item.id} carries no image; quarantining rather than sending an empty upload`);
          await quarantine(item.id, 'unreadable', message, 0);
          blockedItems.push({ id: item.id, title, detail, verdict: 'unreadable', queuedAt: item.queuedAt ?? null, message });
          logEntries.push({ at: Date.now(), title, detail, outcome: 'blocked', message });
          continue;
        }

        rowBlobs = null;   // the bytes are in sendBody now; nothing else holds them
        const timeoutMs = rowKind(item) === 'photo' ? TIMEOUT_PHOTO_MS : TIMEOUT_OTHER_MS;
        let lastErr = null;
        let excused = 0;
        for (let attempt = 0; attempt < SEND_ATTEMPTS; attempt++) {
          if (attempt > 0) await wait(RETRY_DELAYS_MS[Math.min(attempt - 1, RETRY_DELAYS_MS.length - 1)]);
          const nap = napGeneration();
          try {
            res = await sendWithTimeout(f, item.path, {
              method:  item.method,
              headers: sendBody ? { 'Content-Type': 'application/json' } : undefined,
              body:    sendBody ? JSON.stringify(sendBody) : undefined,
            }, timeoutMs);
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            // v0.256.0 — failed because the iPad slept (during the send, or the
            // Wi-Fi not back yet after waking): not a try. Wait until the page is
            // awake and the device online, let the link settle, and send again.
            if (sleptRecently(nap) && excused < SLEEP_EXCUSES) {
              excused++;
              attempt--;
              console.warn(`[outbox] ${item.method} ${item.path} failed around a sleep (${excused}/${SLEEP_EXCUSES}); sending again once awake:`, e && e.message);
              await waitUntilAwake();
              await waitForOnline();
              await wait(SETTLE_AFTER_WAKE_MS);
              continue;
            }
            console.warn(`[outbox] attempt ${attempt + 1}/${SEND_ATTEMPTS} failed for ${item.method} ${item.path}:`, e && e.message);
          }
        }
        if (lastErr) {
          // Network down or the link is too poor to finish — leave the row
          // and stop. The next Sync picks up exactly here.
          console.warn(`[outbox] giving up on ${item.path} for now; aborting drain.`);
          stopped = 'offline';
          break;
        }
      }

      // 2026-09-15 — a DELETE answered 404 is accepted too: what it asked for is
      // already true (removed twice, or by someone else). Quarantining it put
      // "refused" in the sync strip for a photo that was already gone. DELETE
      // only; a refused upload still holds the photo — see outbox-rules.js.
      if (res.ok || refusalMeansDone(item.method, res.status)) {
        // Capture the new row's id BEFORE dropping the queue entry, so the
        // photos queued behind it can find it.
        let response = null;
        let created = null;
        if (sent) {
          response = sent.response ?? null;
          created = sent.created ?? null;
        } else {
          try { response = await res.clone().json(); } catch (_) { /* not JSON, or empty */ }
          created = createdId(item.method, response);
          // v0.256.0 — on record before it leaves the queue (see SENT_KEY).
          recordSent(item, { status: res.status, response, created });
        }
        if (created != null) {
          idMap.set(item.id, created);
          // v0.171.0 — make the mapping DURABLE: rewrite dependants in the DB so
          // a later, separate drain (site mode: photos held for the Wi-Fi sync)
          // still resolves them.
          // v0.256.0 — and if that rewrite fails the sync stops, with this row
          // still queued and on record as sent: the next sync rewrites again.
          try {
            await rewriteRefsInDb(item.id, created);
          } catch (e) {
            console.warn(`[outbox] could not point the changes queued behind row ${item.id} at ${created}; stopping:`, e);
            stopped = 'device';
            break;
          }
        }
        if (!(await removeFromOutbox(item.id))) {
          // Sent, still queued, on record: the next sync removes it unsent.
          stopped = 'device';
          break;
        }
        clearSent(item);
        drained++;
        markApiMutated();
        applied.push({ title, detail });
        logEntries.push({ at: Date.now(), title, detail, outcome: 'applied', message: null });
        notify({ type: 'applied', method: item.method, path: item.path, localId: item.id, response, createdId: created });
        setProgress({ done: drained });
        continue;
      }
      if (res.status >= 400 && res.status < 500) {
        // v0.174.0 — THE ROW STAYS. Until now a 4xx deleted it: "the server
        // said no, replaying won't help". True of the REQUEST, false of the
        // WORK — a refused photo upload still holds the only copy of a photo
        // taken on site. It is quarantined with the server's reason, shown in
        // the sync strip, and removed only when the user says so.
        const body = await res.text().catch(() => '');
        const message = serverMessage(body, res.status);
        console.warn(`[outbox] server refused queued ${item.method} ${item.path}: ${res.status} ${body}`);
        await quarantine(item.id, 'refused', message, res.status);
        blockedItems.push({ id: item.id, title, detail, verdict: 'refused', queuedAt: item.queuedAt ?? null, message });
        rejected.push({ title, detail, status: res.status, message });
        logEntries.push({ at: Date.now(), title, detail, outcome: 'rejected', message });
        continue;
      }
      // 5xx — abort, retry later.
      console.warn(`[outbox] server ${res.status} on ${item.path}; aborting drain.`);
      stopped = 'server';
      break;
    }
  } finally {
    setProgress({ active: false, current: null });
    if (holding) releaseScreen();
  }
  // Ran to the end: nothing to carry on with. Stopped: the banner carries on.
  if (!stopped) clearSyncResume();

  // Write the log BEFORE the final notify. removeFromOutbox() fires its
  // own notify mid-loop, so a subscriber that refreshed on the last
  // removal would have read the log a moment before it was written and
  // shown nothing. This notify is what guarantees the result is seen.
  appendSyncLog(logEntries);
  if (logEntries.length) notify();

  const remaining = await countOutbox();
  const blocked = blockedItems.length;
  return {
    drained,
    remaining,
    pending: Math.max(0, remaining - blocked),
    blocked,
    applied,
    rejected,
    blockedItems,
    stopped,
    held: false,
  };
}

// fetch with a deadline. AbortController where the runtime has one (every
// browser we ship to); a plain race otherwise (tests' fake fetchers).
// v0.256.0 — a request still out when the iPad wakes rarely comes back (its
// connection died in the nap), and the full deadline was minutes. From a wake
// it has AFTER_WAKE_MS to answer; the drain then sends again (a sleep is not
// one of the tries).
async function sendWithTimeout(f, path, init, timeoutMs) {
  const AC = typeof AbortController === 'function' ? AbortController : null;
  const ctrl = AC ? new AC() : null;
  let timer = null;
  let fail = null;
  const deadline = new Promise((_, reject) => { fail = reject; });
  const arm = (ms, message) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (ctrl) { try { ctrl.abort(); } catch (_) {} }
      fail(new Error(message));
    }, ms);
  };
  const endsAt = Date.now() + timeoutMs;
  arm(timeoutMs, `timed out after ${Math.round(timeoutMs / 1000)}s`);
  const off = onPageWake(() => {
    const left = endsAt - Date.now();
    if (left > AFTER_WAKE_MS) arm(AFTER_WAKE_MS, `no answer within ${Math.round(AFTER_WAKE_MS / 1000)}s of waking`);
  });
  try {
    return await Promise.race([f(path, ctrl ? { ...init, signal: ctrl.signal } : init), deadline]);
  } finally {
    clearTimeout(timer);
    off();
  }
}

// Rewrite every remaining row that points at queued row `localId` so it
// carries the real id instead. Idempotent; a row with nothing to rewrite is
// left untouched.
// v0.256.0 — throws when the queue cannot be read or a row cannot be written:
// a photo left pointing at its pin's queued id would be held for ever once the
// pin's row is gone. The drain stops instead, and the next sync rewrites again.
async function rewriteRefsInDb(localId, realId) {
  const map = new Map([[localId, realId]]);
  const rows = await listOutboxRows();
  let changed = false;
  for (const r of rows) {
    if (Number(r.id) === Number(localId)) continue;
    const { body, unresolved } = resolveRefs(r.body, map);
    const before = resolveRefs(r.body, new Map()).unresolved;
    // Something resolved iff the unresolved set shrank.
    if (before.length === unresolved.length) continue;
    if (await updateOutboxRow(r.id, (row) => ({ ...row, body }))) changed = true;
  }
  if (changed) notify();
}

// ── Blobs ─────────────────────────────────────────────────────────
//
// { url: Blob, thumbnailUrl: Blob|null, displayUrl: Blob|null } → the same
// keys as data: URLs merged over the body. Body values win only where the
// blob is absent, so a legacy row with base64 in the body still sends.
async function withBlobsAsDataUrls(body, blobs) {
  if (!blobs || typeof blobs !== 'object') return body;
  const out = { ...(body || {}) };
  for (const [key, stored] of Object.entries(blobs)) {
    if (!stored) continue;
    // v0.174.0 rows hold { bytes, type }; pre-v0.174 rows hold a Blob.
    out[key] = isStoredBytes(stored) ? bytesToDataUrl(stored) : await blobToDataUrl(stored);
  }
  return out;
}

export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    if (typeof FileReader === 'undefined') { reject(new Error('FileReader unavailable')); return; }
    const r = new FileReader();
    r.onload  = () => resolve(String(r.result));
    r.onerror = () => reject(r.error || new Error('blob read failed'));
    r.readAsDataURL(blob);
  });
}

// What kind of row is this, for counting: 'photo' | 'pin' | 'change'.
export function rowKind(item) {
  const p = String(item && item.path || '');
  if (item && (item.blobs || item.blobMeta)) return 'photo';
  if (/^\/api\/defect-photos(\/|\?|$)/.test(p) && String(item.method).toUpperCase() === 'POST') return 'photo';
  if (/^\/api\/defects(\/|\?|$)/.test(p)) return 'pin';
  return 'change';
}

// Approximate bytes a row will send: blob sizes, else body string length.
export function rowBytes(item) {
  let n = 0;
  if (item && item.blobMeta && !item.blobs) {
    for (const m of Object.values(item.blobMeta)) n += (m && Number(m.size)) || 0;
    return n;
  }
  if (item && item.blobs) {
    for (const b of Object.values(item.blobs)) {
      if (!b) continue;
      n += isStoredBytes(b) ? b.bytes.byteLength : (Number(b.size) || 0);
    }
    return n;
  }
  if (item && item.body) {
    try { n = JSON.stringify(item.body).length; } catch (_) { n = 0; }
  }
  return n;
}

// Pull a human sentence out of an error response body. Our API answers
// JSON `{ error }`; Static Web Apps answers plain text. Anything else is
// reported as the bare status rather than dumped raw at the user.
function serverMessage(body, status) {
  const txt = String(body || '').trim();
  if (txt) {
    try {
      const j = JSON.parse(txt);
      if (j && typeof j.error === 'string' && j.error) return j.error.slice(0, 200);
    } catch (_e) {
      if (txt.length <= 200 && !txt.startsWith('<')) return txt;
    }
  }
  return `The server refused this change (HTTP ${status}).`;
}
